import React, { createContext, useContext, useState, useEffect } from 'react';
import type { EmergencyAlertDto, EmergencyAlertSignalMessage } from '@sahaj/types';
import { api, ws } from '../lib/api';
import { topics } from '@sahaj/websocket';
import { useAuth } from './AuthContext';
import { useWorkspace } from './WorkspaceContext';
import { ringtonePlayer } from '../utils/ringtone';

interface EmergencyContextType {
  activeAlerts: EmergencyAlertDto[];
  isSendModalOpen: boolean;
  openSendModal: () => void;
  closeSendModal: () => void;
  acknowledgeAlert: (alertId: string) => Promise<void>;
  refreshActiveAlerts: () => Promise<void>;
}

const EmergencyContext = createContext<EmergencyContextType | undefined>(undefined);

export const EmergencyProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const { currentWorkspace } = useWorkspace();
  const [activeAlerts, setActiveAlerts] = useState<EmergencyAlertDto[]>([]);
  const [isSendModalOpen, setIsSendModalOpen] = useState(false);

  const fetchActiveAlerts = async () => {
    if (!currentWorkspace || !user) return;
    try {
      const alerts = await api.emergencyAlerts.getActive(currentWorkspace.id);
      setActiveAlerts(alerts);
      if (alerts.length > 0) {
        try {
          ringtonePlayer.playEmergencyAlarm();
        } catch (_e) {
          // Autoplay policy fallback: sound fail will never block modal popup
        }
      }
    } catch (err) {
      console.error('[EmergencyContext] Failed to fetch active emergency alerts', err);
    }
  };

  useEffect(() => {
    fetchActiveAlerts();
  }, [currentWorkspace?.id, user?.id]);

  // STOMP WebSocket Listener over /user/queue/alerts
  useEffect(() => {
    if (!user?.id) return;

    const unsubscribe = ws.subscribe(topics.alerts(), async (signal: EmergencyAlertSignalMessage) => {
      console.log('[EmergencyContext] STOMP signal received on /user/queue/alerts:', signal);
      if (signal.signalType === 'EMERGENCY_ALERT_INCOMING') {
        const newAlert: EmergencyAlertDto = {
          id: signal.alertId,
          workspaceId: signal.workspaceId,
          senderId: signal.senderId,
          senderName: signal.senderName,
          senderAvatarUrl: signal.senderAvatarUrl,
          message: signal.message,
          targetScope: signal.targetScope,
          recipientCount: 1,
          acknowledgedCount: 0,
          expiresAt: signal.expiresAt,
          createdAt: signal.createdAt,
        };

        setActiveAlerts((prev) => {
          if (prev.some((a) => a.id === signal.alertId)) return prev;
          return [newAlert, ...prev];
        });

        try {
          ringtonePlayer.playEmergencyAlarm();
        } catch (_e) {
          // Autoplay policy fallback
        }
      }
    });

    return () => {
      unsubscribe();
    };
  }, [user?.id]);

  const acknowledgeAlert = async (alertId: string) => {
    if (!currentWorkspace) return;
    try {
      await api.emergencyAlerts.acknowledge(currentWorkspace.id, alertId);
    } catch (err) {
      console.warn('[EmergencyContext] Acknowledge request error or already acknowledged:', err);
    } finally {
      setActiveAlerts((prev) => {
        const remaining = prev.filter((a) => a.id !== alertId);
        if (remaining.length === 0) {
          ringtonePlayer.stop();
        }
        return remaining;
      });
    }
  };

  const openSendModal = () => setIsSendModalOpen(true);
  const closeSendModal = () => setIsSendModalOpen(false);

  return (
    <EmergencyContext.Provider
      value={{
        activeAlerts,
        isSendModalOpen,
        openSendModal,
        closeSendModal,
        acknowledgeAlert,
        refreshActiveAlerts: fetchActiveAlerts,
      }}
    >
      {children}
    </EmergencyContext.Provider>
  );
};

export const useEmergency = (): EmergencyContextType => {
  const context = useContext(EmergencyContext);
  if (!context) {
    throw new Error('useEmergency must be used within an EmergencyProvider');
  }
  return context;
};
